/**
 * SlideShow
 * @class
 * @constructor
 * @public
 * @version 1.0.0
 */
class SlideShow extends HTMLElement {
  constructor() {
    super();

    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
    this.onNavKeydown = this.onNavKeydown.bind(this);
    this.handleScroll = this.handleScroll.bind(this);

    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    this.navigatedByScroll = false;
    this.scrollTimeout = null;
    this.isScrolling = false;
  }

  /**
   * Observed attributes
   */
  static get observedAttributes() {
    return ["data-active-index"];
  }

  /**
   * Handles when observed attributes changes.
   * @param {string} name - Name of the changed attribute.
   * @param {string} _oldValue - Old value of attribute.
   * @param {string} _newValue - New value of attribute.
   */
  attributeChangedCallback(name, _oldValue, _newValue) {
    switch (name) {
      case "data-active-index":
        this.setActiveItem();
        this.setActiveNavItem();
        this.updateButtons();
        if (!this.navigatedByScroll) {
          this.goto(this.activeIndex);
        }
        this.navigatedByScroll = false;
        break;
      default:
        break;
    }
  }

  /**
   * Getters
   */

  /**
   * @type string
   */
  get id() {
    if (!this.getAttribute("id")) {
      this.setAttribute("id", `id-${Math.random().toString(16).slice(2)}`);
    }
    return /** @type {string} */ (this.getAttribute("id"));
  }

  /**
   * @type HTMLElement
   */
  get slider() {
    return /** @type {HTMLElement} */ (
      this.querySelector(this.getAttribute("slider-selector") || ".slider")
    );
  }

  /**
   * @type HTMLElement[]
   */
  get sliderItems() {
    return Array.from(
      /** @type HTMLCollectionOf<HTMLElement> */ (this.slider.children)
    );
  }

  /**
   * @type HTMLElement
   */
  get sliderNav() {
    return /** @type { HTMLElement} */ (
      this.querySelector(
        this.getAttribute("slider-nav-selector") || ".slider__nav"
      )
    );
  }

  /**
   * @type HTMLElement[]
   */
  get sliderNavItems() {
    return Array.from(
      this.querySelectorAll(
        this.getAttribute("slider-nav-items-selector") || ".slider__nav__button"
      )
    );
  }

  /**
   * @type string
   */
  get buttonNextSelector() {
    return this.getAttribute("data-button-next-selector") || "";
  }

  /**
   * @type string
   */
  get buttonPreviousSelector() {
    return this.getAttribute("data-button-previous-selector") || "";
  }

  /**
   * @type string
   */
  get buttonNextContent() {
    return this.getAttribute("data-button-next-content") || "›";
  }

  /**
   * @type string
   */
  get buttonPreviousContent() {
    return this.getAttribute("data-button-previous-content") || "‹";
  }

  /**
   * @type string
   */
  get buttonNextClasses() {
    return (
      this.getAttribute("data-button-next-classes") ||
      "slide-list__button slide-list__button--next"
    );
  }

  /**
   * @type string
   */
  get buttonPreviousClasses() {
    return (
      this.getAttribute("data-button-previous-classes") ||
      "slide-list__button slide-list__button--previous"
    );
  }

  /**
   * @type string
   */
  get buttonNextAriaLabel() {
    return this.getAttribute("data-button-next-aria-label") || "";
  }

  /**
   * @type string
   */
  get buttonPreviousAriaLabel() {
    return this.getAttribute("data-button-previous-aria-label") || "";
  }

  /**
   * @type string
   */
  get buttonNextAriaHidden() {
    return this.getAttribute("data-button-next-aria-hidden") || "true";
  }

  /**
   * @type string
   */
  get buttonPreviousAriaHidden() {
    return this.getAttribute("data-button-previous-aria-hidden") || "true";
  }

  /**
   * @type HTMLElement
   */
  get nearestElement() {
    if (!this.slider || this.sliderItems.length === 0) return null;

    const containerRect = this.slider.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    let closestElement = this.sliderItems[0];
    let smallestDistance = Infinity;

    this.sliderItems.forEach((slide) => {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;
      const distance = Math.abs(slideCenter - containerCenter);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestElement = slide;
      }
    });

    return /** @type {HTMLElement} */ (closestElement);
  }

  /**
   * @type number
   */
  get nearestIndex() {
    const element = this.nearestElement;
    return element ? this.sliderItems.indexOf(element) : 0;
  }

  /**
   * @type number
   */
  get activeIndex() {
    return this.getAttribute("data-active-index")
      ? parseInt(this.getAttribute("data-active-index") || "0", 10)
      : 0;
  }

  /**
   * setter for value.
   * @param {number} index - New index.
   */
  set activeIndex(index) {
    let verifiedIndex = index;
    if (verifiedIndex < 0) {
      verifiedIndex = 0;
    } else if (verifiedIndex >= this.sliderItems.length) {
      verifiedIndex = this.sliderItems.length - 1;
    }
    this.setAttribute("data-active-index", verifiedIndex.toString());
  }

  /**
   * connectedCallback
   * Render html/css & attatch event listeners
   */
  connectedCallback() {
    this.addAriaAttributes();

    if (this.buttonNextSelector && this.buttonPreviousSelector) {
      this.buttonNext = /** @type {HTMLButtonElement} */ (
        document.querySelector(this.buttonNextSelector)
      );
      this.buttonPrevious = /** @type {HTMLButtonElement} */ (
        document.querySelector(this.buttonPreviousSelector)
      );
    } else {
      this.createButtons();
    }

    if (this.buttonNext) {
      this.buttonNext.addEventListener("click", this.next);
      this.buttonNext.setAttribute("aria-controls", this.sliderItems[0].id);
    }
    if (this.buttonPrevious) {
      this.buttonPrevious.addEventListener("click", this.prev);
    }

    this.sliderNavItems.forEach((item) => {
      // @ts-ignore
      item.addEventListener("click", this.onNavClick);
      // @ts-ignore
      item.addEventListener("keydown", this.onNavKeydown);
    });

    // Use a more reliable scroll detection
    this.slider.addEventListener("scroll", this.handleScroll);

    this.setActiveItem();
    this.setActiveNavItem();
    this.updateButtons();
  }

  /**
   * disconnectedCallback
   * Remove listeners.
   */
  disconnectedCallback() {
    if (this.buttonNext) {
      this.buttonNext.removeEventListener("click", this.next);
    }
    if (this.buttonPrevious) {
      this.buttonPrevious.removeEventListener("click", this.prev);
    }
    this.sliderNavItems.forEach((item) => {
      // @ts-ignore
      item.removeEventListener("click", this.onNavClick);
      // @ts-ignore
      item.removeEventListener("keydown", this.onNavKeydown);
      item.removeAttribute("aria-selected");
    });

    this.slider.removeEventListener("scroll", this.handleScroll);
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  /**
   * Handle scroll events
   */
  handleScroll() {
    if (this.isScrolling) return;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.navigatedByScroll = true;
      this.activeIndex = this.nearestIndex;
    }, 150); // Slightly longer delay for more accuracy
  }

  /**
   * Creates next / previous buttons
   */
  createButtons() {
    this.buttonNext = document.createElement("button");
    this.buttonPrevious = document.createElement("button");

    // Create Font Awesome icons for buttons
    const nextIcon = document.createElement("i");
    nextIcon.setAttribute("class", "fa-regular fa-arrow-right");
    nextIcon.setAttribute("aria-hidden", "true");

    const prevIcon = document.createElement("i");
    prevIcon.setAttribute("class", "fa-regular fa-arrow-left");
    prevIcon.setAttribute("aria-hidden", "true");

    this.buttonNext.appendChild(nextIcon);
    this.buttonPrevious.appendChild(prevIcon);

    if (this.buttonNextAriaLabel) {
      this.buttonNext.setAttribute("aria-label", this.buttonNextAriaLabel);
    }
    if (this.buttonPreviousAriaLabel) {
      this.buttonPrevious.setAttribute(
        "aria-label",
        this.buttonPreviousAriaLabel
      );
    }

    this.buttonNext.setAttribute("aria-hidden", this.buttonNextAriaHidden);
    this.buttonPrevious.setAttribute(
      "aria-hidden",
      this.buttonPreviousAriaHidden
    );
    if (this.buttonNextAriaHidden === "true") {
      this.buttonNext.setAttribute("tabindex", "-1");
    }
    if (this.buttonPreviousAriaHidden === "true") {
      this.buttonPrevious.setAttribute("tabindex", "-1");
    }

    this.buttonNext.setAttribute("class", this.buttonNextClasses);
    this.buttonPrevious.setAttribute("class", this.buttonPreviousClasses);

    this.slider.insertAdjacentElement("afterend", this.buttonNext);
    this.slider.insertAdjacentElement("afterend", this.buttonPrevious);

    this.buttonPrevious.setAttribute("hidden", "true");
    this.buttonNext.setAttribute("hidden", "true");
  }

  /**
   * Update visibility and aria-controls on next / previous buttons
   */
  updateButtons() {
    if (this.activeIndex === 0) {
      if (this.buttonPrevious) {
        this.buttonPrevious.setAttribute("hidden", "true");
      }
    } else {
      if (this.buttonPrevious) {
        this.buttonPrevious.setAttribute(
          "aria-controls",
          this.sliderItems[this.activeIndex - 1].id
        );
        this.buttonPrevious.removeAttribute("hidden");
      }
    }

    if (this.activeIndex === this.sliderItems.length - 1) {
      if (this.buttonNext) {
        this.buttonNext.setAttribute("hidden", "true");
      }
    } else {
      if (this.buttonNext) {
        this.buttonNext.setAttribute(
          "aria-controls",
          this.sliderItems[this.activeIndex + 1].id
        );
        this.buttonNext.removeAttribute("hidden");
      }
    }
  }

  /**
   * Set aria attributes on items and nav
   */
  addAriaAttributes() {
    this.sliderNav.setAttribute("role", "tablist");
    this.sliderNavItems.forEach((item, index) => {
      item.setAttribute("role", "tab");
      item.setAttribute("aria-selected", "false");
      item.setAttribute("id", `${this.id}-nav-${index}`);
      item.setAttribute("aria-controls", `${this.id}-item-${index}`);
      item.setAttribute("type", "button");
    });
    this.sliderItems.forEach((item, index) => {
      item.setAttribute("role", "tabpanel");
      item.setAttribute("id", `${this.id}-item-${index}`);
      item.setAttribute("aria-labelledby", `${this.id}-nav-${index}`);
    });
  }

  /**
   * Next slide
   */
  next() {
    this.isScrolling = true;
    this.activeIndex += 1;
    setTimeout(() => {
      this.isScrolling = false;
    }, 500); // Prevent scroll detection during animation
  }

  /**
   * Previous slide
   */
  prev() {
    this.isScrolling = true;
    this.activeIndex -= 1;
    setTimeout(() => {
      this.isScrolling = false;
    }, 500); // Prevent scroll detection during animation
  }

  /**
   * Navigate to selected slide
   * @param {MouseEvent & { target: HTMLButtonElement }} event - Click event.
   */
  onNavClick(event) {
    event.preventDefault();
    // @ts-ignore
    const index = this.sliderNavItems.indexOf(event.currentTarget);
    if (index !== -1) {
      this.isScrolling = true;
      this.activeIndex = index;
      setTimeout(() => {
        this.isScrolling = false;
      }, 500);
    }
  }

  /**
   * Nav key down
   * @param {KeyboardEvent & { target: HTMLButtonElement }} event - Keydown event.
   */
  onNavKeydown(event) {
    let flag = false;

    switch (event.key) {
      case "ArrowLeft":
        this.isScrolling = true;
        this.activeIndex -= 1;
        flag = true;
        setTimeout(() => {
          this.isScrolling = false;
        }, 500);
        break;

      case "ArrowRight":
        this.isScrolling = true;
        this.activeIndex += 1;
        flag = true;
        setTimeout(() => {
          this.isScrolling = false;
        }, 500);
        break;

      case "Home":
        this.isScrolling = true;
        this.activeIndex = 0;
        flag = true;
        setTimeout(() => {
          this.isScrolling = false;
        }, 500);
        break;

      case "End":
        this.isScrolling = true;
        this.activeIndex = this.sliderItems.length - 1;
        flag = true;
        setTimeout(() => {
          this.isScrolling = false;
        }, 500);
        break;

      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
      this.setActiveNavItem(true);
    }
  }

  /**
   * Goto selected slide
   * @param {number} index - Index to go to.
   */
  goto(index) {
    // Clamp within bounds
    const verifiedIndex = Math.max(
      0,
      Math.min(Math.round(index), this.sliderItems.length - 1)
    );

    // For the last item, ensure it's fully visible
    if (verifiedIndex === this.sliderItems.length - 1) {
      const targetSlide = this.sliderItems[verifiedIndex];
      const slideWidth = targetSlide.offsetWidth;
      const containerWidth = this.slider.clientWidth;
      const totalWidth = this.slider.scrollWidth;

      // Calculate scroll position to show last item fully
      const scrollPosition = totalWidth - containerWidth;

      this.slider.scrollTo({
        top: 0,
        left: scrollPosition,
        behavior: this.prefersReducedMotion ? "instant" : "smooth",
      });
    } else {
      const targetSlide = this.sliderItems[verifiedIndex];
      const left = targetSlide.offsetLeft;

      this.slider.scrollTo({
        top: 0,
        left,
        behavior: this.prefersReducedMotion ? "instant" : "smooth",
      });
    }
  }

  /**
   * Set aria-selected to selected slider nav item.
   * @param {boolean} focus - If selected tab should recieve focus.
   */
  setActiveNavItem(focus) {
    if (typeof focus === "undefined") {
      focus = false;
    }
    this.sliderNavItems.forEach((el, index) => {
      if (index === this.activeIndex) {
        el.setAttribute("aria-selected", "true");
        el.removeAttribute("tabindex");
        if (focus) {
          el.focus();
        }
      } else {
        el.setAttribute("aria-selected", "false");
        el.setAttribute("tabindex", "-1");
      }
    });
  }

  /**
   * Removes aria-hidden and tabindex="-1" on active item.
   */
  setActiveItem() {
    this.sliderItems.forEach((el, index) => {
      if (index === this.activeIndex) {
        el.setAttribute("aria-hidden", "false");
        el.removeAttribute("tabindex");
      } else {
        el.setAttribute("aria-hidden", "true");
        el.setAttribute("tabindex", "-1");
      }
    });
  }
}

if (customElements.get("slide-show") === undefined) {
  window.customElements.define("slide-show", SlideShow);
}
